import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { useSettings } from "../context/SettingsContext";
import { safeSocialHref } from "../lib/socialUrl";
import { toast } from "sonner";

const COLS = [
    { title: "Shop", links: [
        { to: "/category/new-in", label: "New In" },
        { to: "/category/dresses", label: "Dresses" },
        { to: "/category/sets", label: "Sets" },
        { to: "/category/tops", label: "Tops" },
        { to: "/category/accessories", label: "Accessories" },
    ]},
    { title: "Collections", links: [
        { to: "/collections", label: "Summer Collection" },
        { to: "/collections", label: "Festive Collection" },
        { to: "/collections", label: "Everyday Essentials" },
        { to: "/collections", label: "Gift Sets" },
    ]},
    { title: "Help", links: [
        { to: "/about", label: "Size Guide" },
        { to: "/about", label: "Shipping & Delivery" },
        { to: "/about", label: "Returns & Exchanges" },
        { to: "/contact", label: "FAQ" },
    ]},
    { title: "About", links: [
        { to: "/about", label: "Our Story" },
        { to: "/about", label: "Careers" },
        { to: "/contact", label: "Contact Us" },
    ]},
];

export default function Footer() {
    const { settings } = useSettings();
    const [email, setEmail] = useState("");

    const subscribe = (e) => {
        e.preventDefault();
        if (!email) return;
        toast("Thank you for joining the Loomara family ✨");
        setEmail("");
    };

    return (
        <footer className="bg-ivory-100 border-t border-sage-300/30 mt-20" data-testid="site-footer">
            <div className="container-boutique py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    {/* Logo + tagline */}
                    <div className="md:col-span-3">
                        <Logo />
                        <p className="mt-4 text-sm text-ink-500 italic font-serif leading-relaxed">
                            Joyfully designed<br/>from the first crawl.
                        </p>
                    </div>

                    {/* Columns */}
                    {COLS.map((col) => (
                        <div key={col.title} className="md:col-span-2">
                            <h4 className="text-xs uppercase tracking-[0.28em] text-sage-700 mb-4 font-medium">{col.title}</h4>
                            <ul className="space-y-2.5 text-sm text-ink-700">
                                {col.links.map((l) => (
                                    <li key={l.label}>
                                        <Link to={l.to} className="hover:text-sage-600 transition-colors">{l.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    {/* Newsletter */}
                    <div className="md:col-span-3">
                        <h4 className="text-xs uppercase tracking-[0.28em] text-sage-700 mb-4 font-medium">Stay in the Loop</h4>
                        <p className="text-sm text-ink-500 mb-4">Sign up for sweet updates & special offers.</p>
                        <form onSubmit={subscribe} className="flex gap-2 items-center bg-white border border-sage-300/40 rounded-full pl-4 pr-1 py-1" data-testid="newsletter-form">
                            <Mail size={14} className="text-sage-600" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                className="flex-1 bg-transparent border-none focus:outline-none text-sm text-ink-900 placeholder:text-ink-500 py-2"
                                data-testid="newsletter-input"
                            />
                            <button type="submit" className="bg-sage-400 hover:bg-sage-500 text-white rounded-full h-9 w-9 flex items-center justify-center transition-colors" data-testid="newsletter-submit">
                                <ArrowRight size={16} />
                            </button>
                        </form>

                        <div className="flex items-center gap-3 mt-6 text-sage-700">
                            <a href={safeSocialHref(settings?.instagramUrl)} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full border border-sage-300/40 flex items-center justify-center hover:bg-sage-400 hover:text-white transition-colors" aria-label="Instagram" data-testid="social-instagram">
                                <Instagram size={15} strokeWidth={1.5} />
                            </a>
                            <a href={safeSocialHref(settings?.facebookUrl)} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full border border-sage-300/40 flex items-center justify-center hover:bg-sage-400 hover:text-white transition-colors" aria-label="Facebook" data-testid="social-facebook">
                                <Facebook size={15} strokeWidth={1.5} />
                            </a>
                            <a href={safeSocialHref(settings?.pinterestUrl)} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full border border-sage-300/40 flex items-center justify-center hover:bg-sage-400 hover:text-white transition-colors text-xs font-serif italic" aria-label="Pinterest" data-testid="social-pinterest">
                                P
                            </a>
                            <Link to="/contact" className="h-9 w-9 rounded-full border border-sage-300/40 flex items-center justify-center hover:bg-sage-400 hover:text-white transition-colors" aria-label="Email" data-testid="social-email">
                                <Mail size={14} strokeWidth={1.5} />
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="dotted-rule mt-14 opacity-60" />
                <p className="text-center text-xs text-ink-500 mt-6 tracking-wider">
                    © {new Date().getFullYear()} Loomara Kids Wear. All rights reserved. <span className="text-blush-400">♥</span>
                </p>
            </div>
        </footer>
    );
}
